import React, { useEffect, useMemo, useState } from 'react';
import { formatNoteTimestamp, isRecentlySavedNote, noteTimestamp } from '../../utils/noteDate';

// Some legacy notes were stored with the author + timestamp baked into the
// `text` field (e.g. "Jason (2026-05-15 15:22:42) - body of the note"),
// leaving `created_by` as "Unknown" and `created_on` as a parse of the
// outer wrapping prefix instead of the real note timestamp. Normalise each
// note for display: lift the embedded author into `created_by` (if blank/
// Unknown), lift the embedded timestamp into `created_on`, and strip the
// prefix from the visible text so the Notes column shows only the body.
const PREFIX_RE = /^\s*([^()\n]{1,80}?)\s*\((\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?)\)\s*[-–:]\s*([\s\S]*)$/;

const normaliseNote = (n) => {
  const out = { ...n };
  const text = String(out.text || '');
  const m = text.match(PREFIX_RE);
  if (m) {
    const isBlankAuthor = !out.created_by || /^unknown$/i.test(String(out.created_by).trim());
    if (isBlankAuthor) out.created_by = m[1].trim();
    // The embedded timestamp is the authoritative one — prefer it over the
    // outer wrapper date that the legacy importer guessed.
    out.created_on = m[2].trim();
    out.text = m[3].trim();
  }
  return out;
};

const DetailNotesTab = ({
  notes,
  uploadRequired,
  showNoteForm, setShowNoteForm,
  newNoteText, setNewNoteText,
  noteUrgent, setNoteUrgent,
  handleAddNote,
}) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 15 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  // Normalise + sort once per render. Most-recent-first chronological order
  // (matches the legacy site and how users described the expected behaviour).
  const displayNotes = useMemo(() => {
    const list = Array.isArray(notes) ? notes.map(normaliseNote) : [];
    list.sort((a, b) => noteTimestamp(b) - noteTimestamp(a));
    return list;
  }, [notes]);

  return (
    <div className="bg-white rounded shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl text-[#5B8DB8]">Project Notes</h2>
        {uploadRequired && (
          <span className="text-red-600 font-bold text-lg" data-testid="upload-required-banner">Upload Required!</span>
        )}
        <button onClick={() => setShowNoteForm(true)} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded transition-colors flex items-center gap-2" data-testid="add-note-btn">
          <span>+</span> Add Note
        </button>
      </div>
      {showNoteForm && (
        <div className="mb-6 p-4 border border-gray-200 rounded bg-gray-50">
          <textarea value={newNoteText} onChange={(e) => setNewNoteText(e.target.value)} placeholder="Enter note..." className="border border-gray-300 rounded px-3 py-2 w-full h-32 text-sm resize-none" data-testid="note-textarea" />
          <div className="flex items-center gap-2 mt-3">
            <button onClick={handleAddNote} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-1 rounded text-sm" data-testid="save-note-btn">Save Note</button>
            <button onClick={() => { setShowNoteForm(false); setNewNoteText(''); setNoteUrgent(false); }} className="border border-gray-300 hover:bg-gray-100 text-gray-600 px-4 py-1 rounded text-sm">Cancel</button>
            <label className="flex items-center gap-1.5 text-sm text-gray-600 ml-2 cursor-pointer">
              <input type="checkbox" checked={noteUrgent} onChange={(e) => setNoteUrgent(e.target.checked)} className="w-4 h-4" data-testid="note-urgent-checkbox" />
              Mark as Urgent
            </label>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2">
              <th className="text-left p-3 text-gray-600 font-normal w-28">Created By</th>
              <th className="text-left p-3 text-gray-600 font-normal w-28">Created On</th>
              <th className="text-left p-3 text-gray-600 font-normal">Notes</th>
            </tr>
          </thead>
          <tbody>
            {displayNotes.length === 0 ? (
              <tr><td colSpan="3" className="p-4 text-center text-gray-500 italic">No notes found</td></tr>
            ) : displayNotes.map((note, idx) => {
              // Atomic notes use created_at (UTC) and display in the viewer's
              // local timezone; legacy notes retain their existing created_on.
              // Two legacy storage shapes occur
              // in the DB: the friendly "5/15/2026 9:17am" (what the new UI
              // writes) and the ISO "2026-05-15 15:47:30" (what some legacy/
              // OCR-imported notes have). Render both as M/D/YYYY + h:mma
              // on two lines.
              const raw = formatNoteTimestamp(note);
              let datePart = '';
              let timePart = '';
              if (raw) {
                // ISO shape (starts with YYYY-MM-DD) → reformat via Date.
                const isISO = /^\d{4}-\d{2}-\d{2}/.test(raw);
                if (isISO) {
                  const d = new Date(raw.replace(' ', 'T'));
                  if (!Number.isNaN(d.getTime())) {
                    datePart = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
                    timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase().replace(/\s+/g, '');
                  }
                }
                // Already-friendly or anything else → just split on the first
                // space (matches the original on-screen behaviour).
                if (!datePart) {
                  const parts = raw.split(' ');
                  datePart = parts[0] || '';
                  timePart = parts.slice(1).join(' ') || '';
                }
              }
              const displayAuthor = note.created_by && String(note.created_by).trim() ? note.created_by : 'Unknown';
              const isFreshlySaved = isRecentlySavedNote(note, currentTime);
              return (
                <tr key={idx} className={`border-b align-top ${note.urgent ? 'text-red-600' : ''}`} data-testid={`note-row-${idx}`}>
                  <td className={`p-3 ${note.urgent ? 'text-red-600' : 'text-gray-700'}`}>{displayAuthor}</td>
                  <td className={`p-3 ${note.urgent ? 'text-red-600' : 'text-gray-700'}`}>
                    <div>{datePart}</div>
                    {timePart && <div className={note.urgent ? 'text-red-400' : 'text-gray-500'}>{timePart}</div>}
                  </td>
                  <td className={`p-3 ${note.urgent ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                    <div className="flex items-start gap-2">
                      <p>{note.text}</p>
                      {isFreshlySaved && (
                        <span className="shrink-0 bg-[#E6F7F8] text-[#277A83] border border-[#9BDBE0] px-2 py-0.5 text-xs font-medium" data-testid={`note-updated-just-now-${idx}`}>
                          Updated just now
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DetailNotesTab;
